import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from "@/components/ui/alert-dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  FolderArchive, Upload, Trash2, Download, Loader2, MoreVertical, Search,
  File, Brain, Folder, FolderPlus, ChevronRight, Home, Pencil, FolderInput, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryLabels = {
  general: "Général",
  contract: "Contrat",
  legal: "Juridique",
  invoice: "Facture",
  correspondence: "Correspondance",
  hr: "Ressources Humaines",
  commercial: "Commercial"
};
const categoryOptions = Object.keys(categoryLabels);

function formatSize(bytes) {
  if (!bytes) return "N/A";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "text-red-500";
  if (["docx", "doc"].includes(ext)) return "text-blue-500";
  if (["xlsx", "xls", "csv"].includes(ext)) return "text-green-500";
  if (["jpg", "jpeg", "png", "gif"].includes(ext)) return "text-purple-500";
  return "text-slate-400";
}

export default function Documents({ user, setUser }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const fileInputRef = useRef(null);

  // Dialog states
  const [folderDialog, setFolderDialog] = useState({ open: false, mode: "create", id: null });
  const [folderName, setFolderName] = useState("");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const [moveTarget, setMoveTarget] = useState("root");

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(API_URL + "/api/folders", { credentials: "include" });
      if (res.ok) setFolders(await res.json());
    } catch (e) { /* ignore */ }
  }, []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("folder_id", currentFolderId || "root");
      if (filterCategory && filterCategory !== "all") params.append("category", filterCategory);
      const res = await fetch(API_URL + "/api/documents?" + params.toString(), { credentials: "include" });
      if (res.ok) setDocuments(await res.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, [currentFolderId, filterCategory]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Build breadcrumb path from root to current folder
  const buildPath = () => {
    const path = [];
    let id = currentFolderId;
    const byId = {};
    folders.forEach((f) => { byId[f.folder_id] = f; });
    while (id && byId[id]) {
      path.unshift(byId[id]);
      id = byId[id].parent_id;
    }
    return path;
  };
  const breadcrumbPath = buildPath();
  const subFolders = folders.filter((f) => (f.parent_id || null) === (currentFolderId || null));

  async function submitFolder() {
    const name = folderName.trim();
    if (!name) { toast.error("Nom requis"); return; }
    try {
      if (folderDialog.mode === "create") {
        const res = await fetch(API_URL + "/api/folders", {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, parent_id: currentFolderId })
        });
        if (res.ok) { toast.success("Dossier créé"); } else { toast.error("Erreur création"); }
      } else {
        const res = await fetch(API_URL + "/api/folders/" + folderDialog.id, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name })
        });
        if (res.ok) { toast.success("Dossier renommé"); } else { toast.error("Erreur"); }
      }
      setFolderDialog({ open: false, mode: "create", id: null });
      setFolderName("");
      fetchFolders();
    } catch (e) { toast.error("Erreur"); }
  }

  async function confirmDeleteFolder() {
    if (!deleteFolderTarget) return;
    try {
      const res = await fetch(API_URL + "/api/folders/" + deleteFolderTarget.folder_id, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Dossier supprimé (${data.deleted_documents} document(s))`);
        if (currentFolderId === deleteFolderTarget.folder_id) setCurrentFolderId(deleteFolderTarget.parent_id || null);
        fetchFolders();
        fetchDocuments();
      } else { toast.error("Erreur suppression"); }
    } catch (e) { toast.error("Erreur"); }
    setDeleteFolderTarget(null);
  }

  async function handleUpload(e) {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", "general");
      formData.append("description", "");
      if (currentFolderId) formData.append("folder_id", currentFolderId);
      try {
        const res = await fetch(API_URL + "/api/documents/upload", {
          method: "POST", credentials: "include", body: formData
        });
        if (res.ok) { toast.success("\"" + file.name + "\" uploadé"); }
        else { toast.error("Erreur pour \"" + file.name + "\""); }
      } catch (err) { toast.error("Erreur d'upload"); }
    }
    fileInputRef.current.value = "";
    setUploading(false);
    fetchDocuments();
    fetchFolders();
  }

  async function handleDownload(doc) {
    try {
      const res = await fetch(API_URL + "/api/documents/" + doc.document_id + "/download", { credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = doc.original_filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { toast.error("Erreur de téléchargement"); }
  }

  async function handleDelete(docId) {
    try {
      const res = await fetch(API_URL + "/api/documents/" + docId, { method: "DELETE", credentials: "include" });
      if (res.ok) {
        setDocuments((prev) => prev.filter((d) => d.document_id !== docId));
        toast.success("Document supprimé");
        fetchFolders();
      }
    } catch (e) { toast.error("Erreur"); }
  }

  async function confirmMove() {
    if (!moveDoc) return;
    try {
      const res = await fetch(API_URL + "/api/documents/" + moveDoc.document_id + "/move", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_id: moveTarget === "root" ? null : moveTarget })
      });
      if (res.ok) { toast.success("Document déplacé"); fetchDocuments(); fetchFolders(); }
      else { toast.error("Erreur déplacement"); }
    } catch (e) { toast.error("Erreur"); }
    setMoveDoc(null);
    setMoveTarget("root");
  }

  const filteredDocs = documents.filter((d) =>
    !searchQuery || d.original_filename.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="documents-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Documents</h1>
            <p className="text-sm text-muted-foreground">Organisez vos documents par dossiers</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="file" ref={fileInputRef} onChange={handleUpload} className="hidden" multiple
              accept=".pdf,.docx,.doc,.xlsx,.csv,.txt,.jpg,.jpeg,.png" data-testid="file-input"
            />
            <Button
              variant="outline"
              onClick={() => { setFolderDialog({ open: true, mode: "create", id: null }); setFolderName(""); }}
              data-testid="new-folder-btn"
            >
              <FolderPlus className="w-4 h-4 mr-2" /> Nouveau dossier
            </Button>
            <Button onClick={() => fileInputRef.current.click()} disabled={uploading} data-testid="upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Uploader
            </Button>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-sm mb-4 flex-wrap" data-testid="breadcrumb">
          <button
            onClick={() => setCurrentFolderId(null)}
            className="flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="breadcrumb-root"
          >
            <Home className="w-4 h-4" /> Racine
          </button>
          {breadcrumbPath.map((f) => (
            <span key={f.folder_id} className="flex items-center gap-1">
              <ChevronRight className="w-4 h-4 text-slate-300" />
              <button
                onClick={() => setCurrentFolderId(f.folder_id)}
                className="px-2 py-1 rounded hover:bg-slate-100 transition-colors font-medium"
                data-testid={"breadcrumb-" + f.folder_id}
              >
                {f.name}
              </button>
            </span>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="flex gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un document..." className="pl-9" data-testid="search-input"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[220px]" data-testid="category-filter">
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categoryOptions.map((c) => (
                <SelectItem key={c} value={c}>{categoryLabels[c]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Sub-folders */}
        {subFolders.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6" data-testid="folders-grid">
            {subFolders.map((f) => (
              <div
                key={f.folder_id}
                className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 transition-colors cursor-pointer bg-white"
                onClick={() => setCurrentFolderId(f.folder_id)}
                data-testid={"folder-" + f.folder_id}
              >
                <Folder className="w-5 h-5 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-xs text-muted-foreground">{f.document_count || 0} fichier(s)</p>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" data-testid={"folder-menu-" + f.folder_id}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => { setFolderDialog({ open: true, mode: "rename", id: f.folder_id }); setFolderName(f.name); }}>
                      <Pencil className="w-4 h-4 mr-2" /> Renommer
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => setDeleteFolderTarget(f)} data-testid={"delete-folder-" + f.folder_id}>
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}

        {/* Documents list */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : filteredDocs.length === 0 && subFolders.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FolderArchive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-1">Dossier vide</h3>
              <p className="text-sm text-muted-foreground mb-4">Uploadez un document ou créez un sous-dossier</p>
              <Button variant="outline" onClick={() => fileInputRef.current.click()}>
                <Upload className="w-4 h-4 mr-2" /> Uploader un document
              </Button>
            </CardContent>
          </Card>
        ) : filteredDocs.length === 0 ? null : (
          <div className="space-y-2" data-testid="documents-list">
            {filteredDocs.map((doc) => (
              <div
                key={doc.document_id}
                className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 transition-colors bg-white"
                data-testid={"doc-" + doc.document_id}
              >
                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center">
                  <File className={"w-5 h-5 " + getFileIcon(doc.original_filename)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.original_filename}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span>{formatSize(doc.size)}</span>
                    <span>{new Date(doc.created_at).toLocaleDateString("fr-FR")}</span>
                    <Badge variant="secondary" className="text-xs">{categoryLabels[doc.category] || doc.category}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/ai/analysis")} title="Analyser avec IA" data-testid={"analyze-doc-" + doc.document_id}>
                    <Brain className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)} data-testid={"download-doc-" + doc.document_id}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={"doc-menu-" + doc.document_id}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { setMoveDoc(doc); setMoveTarget(doc.folder_id || "root"); }} data-testid={"move-doc-" + doc.document_id}>
                        <FolderInput className="w-4 h-4 mr-2" /> Déplacer
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(doc.document_id)} data-testid={"delete-doc-" + doc.document_id}>
                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Rename folder dialog */}
      <Dialog open={folderDialog.open} onOpenChange={(o) => setFolderDialog((p) => ({ ...p, open: o }))}>
        <DialogContent data-testid="folder-dialog">
          <DialogHeader>
            <DialogTitle>{folderDialog.mode === "create" ? "Nouveau dossier" : "Renommer le dossier"}</DialogTitle>
            <DialogDescription>
              {folderDialog.mode === "create"
                ? "Le dossier sera créé dans l'emplacement actuel."
                : "Modifiez le nom du dossier."}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={folderName} onChange={(e) => setFolderName(e.target.value)}
            placeholder="Nom du dossier" autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") submitFolder(); }}
            data-testid="folder-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFolderDialog({ open: false, mode: "create", id: null })}>Annuler</Button>
            <Button onClick={submitFolder} data-testid="folder-submit-btn">
              {folderDialog.mode === "create" ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move document dialog */}
      <Dialog open={!!moveDoc} onOpenChange={(o) => { if (!o) setMoveDoc(null); }}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader>
            <DialogTitle>Déplacer le document</DialogTitle>
            <DialogDescription>Choisissez le dossier de destination.</DialogDescription>
          </DialogHeader>
          <Select value={moveTarget} onValueChange={setMoveTarget}>
            <SelectTrigger data-testid="move-target-select">
              <SelectValue placeholder="Choisir un dossier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="root">Racine</SelectItem>
              {folders.map((f) => (
                <SelectItem key={f.folder_id} value={f.folder_id}>{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDoc(null)}>Annuler</Button>
            <Button onClick={confirmMove} data-testid="move-confirm-btn">Déplacer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete folder confirmation */}
      <AlertDialog open={!!deleteFolderTarget} onOpenChange={(o) => { if (!o) setDeleteFolderTarget(null); }}>
        <AlertDialogContent data-testid="delete-folder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" /> Supprimer le dossier ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action supprimera définitivement le dossier
              {deleteFolderTarget ? ` « ${deleteFolderTarget.name} »` : ""}, tous ses sous-dossiers
              ainsi que tous les documents qu'ils contiennent. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFolder} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-folder-btn">
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
